import { useRef } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { DotsLoader } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import CompanyAutocompleteItem from "app/pages/sales-intelligence/modules/company-autocomplete/components/CompanyAutocompleteItem/CompanyAutocompleteItem";
import NoSearchResults from "app/pages/sales-intelligence/modules/company-autocomplete/components/NoSearchResults/NoSearchResults";
import useCompanyAutocompleteCustomFetch from "app/pages/sales-intelligence/modules/company-autocomplete/queries/useCompanyAutocompleteCustomFetch";
import type { IAutoCompleteSiteItem } from "app/@types/autocomplete";

import { StyledCompanyAutocomplete, StyledCompanyPlaceholderText, StyledItemsContainer } from "./styles";

type CompanyAutocompleteResult = IAutoCompleteSiteItem[] | { noData: true };

const MAX_ITEMS_TO_SHOW = 5;

const MeetingCompanyAutocomplete = ({
    onItemClick,
    isDisabled = false,
}: {
    onItemClick: (domain: string, isCompanyLevel: boolean) => void;
    isDisabled?: boolean;
}) => {
    const scrollAreaRef = useRef<HTMLDivElement | null>(null);
    const { getListItems, isLoading } = useCompanyAutocompleteCustomFetch();

    const handleGetListItems = async (query: string): Promise<CompanyAutocompleteResult> => {
        const term = query.trim();
        if (term.length === 0) {
            return [];
        }
        const data = await getListItems(term);
        scrollAreaRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        if (data.length === 0) {
            return { noData: true };
        }
        return data;
    };

    const handleItemClick = (domain: string, companyName?: string, isCompanyLevel?: boolean) => {
        AIAgentTrackingService.meeting.trackSelectCompany(companyName ?? domain);
        if (domain) {
            onItemClick(domain, Boolean(isCompanyLevel));
        }
    };

    const renderItems = ({ listItems, query }: { listItems: CompanyAutocompleteResult; query: string }) => {
        const term = query ? query.trim() : "";
        if (term.length === 0) {
            return null;
        }
        if (Array.isArray(listItems)) {
            const cleanedTerm = term.toLowerCase().replace(/,/g, " ");

            return listItems.length > 0 ? (
                <div className="ListItemsContainer">
                    <StyledItemsContainer
                        ref={scrollAreaRef}
                        withScrollbar={listItems.length > MAX_ITEMS_TO_SHOW}
                        maxItemsToShow={MAX_ITEMS_TO_SHOW}
                    >
                        {listItems.map((company) => (
                            <CompanyAutocompleteItem
                                key={company.name}
                                company={company}
                                queryWords={cleanedTerm.split(" ").filter(Boolean)}
                                onCompanyClick={() =>
                                    handleItemClick(company.companyInfo?.mainDomain ?? "", company.name, true)
                                }
                                onWebsiteClick={() => handleItemClick(company.companyInfo?.mainDomain ?? "")}
                                isHideDataCreditChips
                            />
                        ))}
                    </StyledItemsContainer>
                </div>
            ) : null;
        }

        if (listItems.noData) {
            return (
                <NoSearchResults
                    className="ListItemsContainer"
                    searchTerm={term}
                />
            );
        }
    };

    return (
        <StyledCompanyAutocomplete
            searchIcon={null}
            placeholder={
                <StyledCompanyPlaceholderText>
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[200]}
                    >
                        {translate("si.pages.ai_agent.search.autocomplete.placeholder")}
                    </Text>
                </StyledCompanyPlaceholderText>
            }
            loadingComponent={<DotsLoader />}
            floating
            debounce={250}
            getListItems={handleGetListItems}
            renderItems={renderItems}
            isLoading={isLoading}
            disabled={isDisabled}
        />
    );
};

export default MeetingCompanyAutocomplete;
